import React, { useState } from "react";
import { useData } from "../../context/DataContext";
import Card from "../ui/Card";
import Table from "../ui/Table";
import Badge from "../ui/Badge";
import EngagementChart from "./EngagementChart";

const EngagementTracker = () => {
  const { getEngagementData, getPlaybookEngagementStats } = useData();
  const [expandedPlaybook, setExpandedPlaybook] = useState(null);

  const engagementData = getEngagementData();
  const engagementStats = getPlaybookEngagementStats();

  // Prepare chart data
  const chartData = Object.values(engagementStats).map((stat) => ({
    playbookName: stat.playbookName.replace(".pdf", ""),
    openRate: stat.openRate,
    assignedCount: stat.assignedCount,
    openedCount: stat.openedCount,
  }));

  const formatDateTime = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return (
      date.toLocaleDateString() +
      " " +
      date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      })
    );
  };

  const engagementColumns = [
    {
      header: "Playbook Name",
      accessor: "playbookName",
      width: "25%",
    },
    {
      header: "Player Name",
      accessor: "playerName",
      width: "20%",
    },
    {
      header: "Status",
      accessor: "opened",
      width: "12%",
      render: (engagement) => (
        <Badge variant={engagement.opened ? "success" : "warning"}>
          {engagement.opened ? "Opened" : "Not Opened"}
        </Badge>
      ),
    },
    {
      header: "Time Spent",
      accessor: "totalTimeSpentMinutes",
      width: "13%",
      render: (engagement) => (
        <span
          className={engagement.totalTimeSpentMinutes > 0 ? "" : "text-muted"}
        >
          {engagement.totalTimeSpentMinutes > 0
            ? `${engagement.totalTimeSpentMinutes} min`
            : "—"}
        </span>
      ),
    },
    {
      header: "Last Opened",
      accessor: "lastOpened",
      width: "30%",
      render: (engagement) => (
        <span className={engagement.lastOpened ? "" : "text-muted"}>
          {formatDateTime(engagement.lastOpened)}
        </span>
      ),
    },
  ];

  const statsColumns = [
    {
      header: "Playbook",
      accessor: "playbookName",
      width: "40%",
    },
    {
      header: "Assigned",
      accessor: "assignedCount",
      width: "15%",
      render: (stat) => (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "2rem",
            height: "2rem",
            backgroundColor: "#64748b",
            color: "white",
            borderRadius: "50%",
            fontSize: "0.875rem",
            fontWeight: 600,
          }}
        >
          {stat.assignedCount}
        </span>
      ),
    },
    {
      header: "Opened",
      accessor: "openedCount",
      width: "15%",
      render: (stat) => (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "2rem",
            height: "2rem",
            backgroundColor: "#16a34a",
            color: "white",
            borderRadius: "50%",
            fontSize: "0.875rem",
            fontWeight: 600,
          }}
        >
          {stat.openedCount}
        </span>
      ),
    },
    {
      header: "Open Rate",
      accessor: "openRate",
      width: "30%",
      render: (stat) => (
        <div className="progress-container-inline">
          <div className="progress-bar-bg">
            <div
              className="progress-bar-fill"
              style={{
                width: `${stat.openRate}%`,
                backgroundColor:
                  stat.openRate >= 70
                    ? "#16a34a"
                    : stat.openRate >= 40
                    ? "#f59e0b"
                    : "#dc2626",
              }}
            />
          </div>
          <span
            className="progress-text"
            style={{
              color:
                stat.openRate >= 70
                  ? "#16a34a"
                  : stat.openRate >= 40
                  ? "#f59e0b"
                  : "#dc2626",
            }}
          >
            {stat.openRate}%
          </span>
        </div>
      ),
    },
  ];

  return (
    <div className="engagement-container">
      {/* Engagement Statistics */}
      <Card title="Playbook Engagement Overview">
        {Object.keys(engagementStats).length === 0 ? (
          <div className="engagement-section">
            <div className="engagement-icon">📊</div>
            <h3 className="engagement-title">No engagement data yet</h3>
            <p>Upload and assign playbooks to see engagement statistics</p>
          </div>
        ) : (
          <>
            <Table
              columns={statsColumns}
              data={Object.values(engagementStats)}
            />

            {/* Engagement Chart */}
            {chartData.length > 0 && (
              <div className="engagement-button-container">
                <h4 className="section-title">Open Rate by Playbook</h4>
                <EngagementChart data={chartData} />
              </div>
            )}
          </>
        )}
      </Card>

      {/* Detailed Engagement Data */}
      <Card title="Detailed Engagement Tracking">
        {engagementData.length === 0 ? (
          <div className="engagement-section">
            <div className="engagement-icon">📋</div>
            <h3 className="engagement-title">No detailed data available</h3>
            <p>
              Assign playbooks to players to see detailed engagement tracking
            </p>
          </div>
        ) : (
          <div className="playbook-engagement-list">
            {(() => {
              // Group engagement data by playbook
              const playbookGroups = {};
              engagementData.forEach((item) => {
                const playbookName = item.playbookName;
                if (!playbookGroups[playbookName]) {
                  playbookGroups[playbookName] = [];
                }
                playbookGroups[playbookName].push(item);
              });

              return Object.entries(playbookGroups).map(
                ([playbookName, players]) => {
                  // Sort players by time spent: green (≥15 min) first, then yellow (1-15 min), then red (<1 min)
                  const sortedPlayers = [...players].sort((a, b) => {
                    const timeA = a.totalTimeSpentMinutes;
                    const timeB = b.totalTimeSpentMinutes;
                    
                    // Assign priority: green=3, yellow=2, red=1
                    const getPriority = (time) => {
                      if (time >= 15) return 3; // green
                      if (time >= 1) return 2;  // yellow
                      return 1;                  // red
                    };
                    
                    const priorityA = getPriority(timeA);
                    const priorityB = getPriority(timeB);
                    
                    // Sort by priority (descending), then by time spent (descending)
                    if (priorityA !== priorityB) {
                      return priorityB - priorityA;
                    }
                    return timeB - timeA;
                  });
                  
                  const isExpanded = expandedPlaybook === playbookName;

                  return (
                    <div key={playbookName} className="playbook-group">
                      <div
                        className="playbook-header"
                        onClick={() => {
                          // Toggle: if already expanded, close it; otherwise, open this one
                          setExpandedPlaybook(
                            isExpanded ? null : playbookName
                          );
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "0.875rem 1rem",
                          cursor: "pointer",
                          backgroundColor: "#f8fafc",
                          borderRadius: "0.5rem",
                          marginBottom: isExpanded ? "0.5rem" : "0.75rem",
                          transition: "all 0.2s ease",
                          border: "1px solid #e2e8f0",
                          gap: "0.75rem",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            minWidth: 0,
                            flex: 1,
                          }}
                        >
                          <span
                            style={{
                              fontSize: "1rem",
                              transition: "transform 0.2s ease",
                              transform: isExpanded
                                ? "rotate(90deg)"
                                : "rotate(0deg)",
                              flexShrink: 0,
                            }}
                          >
                            ▶
                          </span>
                          <span
                            style={{
                              fontWeight: 700,
                              fontSize: "0.95rem",
                              color: "#0f172a",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {playbookName}
                          </span>
                        </div>
                        <Badge variant="secondary">
                          {sortedPlayers.length}{" "}
                          {sortedPlayers.length === 1 ? "player" : "players"}
                        </Badge>
                      </div>

                      {isExpanded && (
                        <div
                          className="playbook-players"
                          style={{
                            marginBottom: "1rem",
                            backgroundColor: "#ffffff",
                            borderRadius: "0.5rem",
                            border: "1px solid #e2e8f0",
                            overflow: "hidden",
                            maxHeight: "290px",
                            overflowY: "auto",
                          }}
                        >
                          {sortedPlayers.map((player, index) => {
                            const timeSpent = player.totalTimeSpentMinutes;
                            let dotColor = "#dc2626"; // red - less than 1 minute
                            if (timeSpent >= 15) {
                              dotColor = "#16a34a"; // green - 15+ minutes
                            } else if (timeSpent >= 1) {
                              dotColor = "#eab308"; // yellow - 1-15 minutes
                            }

                            const formatDateTime = (dateString) => {
                              if (!dateString) return "Never Opened";
                              const date = new Date(dateString);
                              return (
                                date.toLocaleDateString() +
                                " " +
                                date.toLocaleTimeString("en-US", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              );
                            };

                            const formatTimeSpent = (minutes) => {
                              if (minutes === 0) return "0 min";
                              if (minutes < 1) return "< 1 min";
                              return `${minutes} min`;
                            };

                            return (
                              <div
                                key={player.id}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  padding: "1rem 1.25rem",
                                  borderBottom:
                                    index < sortedPlayers.length - 1
                                      ? "1px solid #f1f5f9"
                                      : "none",
                                  gap: "1rem",
                                }}
                              >
                                <div
                                  style={{
                                    width: "12px",
                                    height: "12px",
                                    borderRadius: "50%",
                                    backgroundColor: dotColor,
                                    flexShrink: 0,
                                  }}
                                />
                                <div
                                  style={{
                                    flex: 1,
                                    minWidth: 0,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontWeight: 500,
                                      fontSize: "0.95rem",
                                      color: "#0f172a",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {player.playerName}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    fontSize: "0.875rem",
                                    color: "#64748b",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {formatDateTime(player.lastOpened)}
                                </div>
                                <div
                                  style={{
                                    fontSize: "0.875rem",
                                    color: "#64748b",
                                    fontWeight: 500,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {formatTimeSpent(timeSpent)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }
              );
            })()}
          </div>
        )}
      </Card>
    </div>
  );
};

export default EngagementTracker;
